import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

const MapModuleToDegree = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    selectedModule: '',
    selectedModuleId: '', // Store the actual module ID
    selectedDegreePrograms: [],
    year: '',
    isMandatory: true
  });

  // Data lists
  const [modulesList, setModulesList] = useState([]);
  const [degreeProgramsList, setDegreeProgramsList] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const [showModuleDropdown, setShowModuleDropdown] = useState(false);
  const [moduleSearchQuery, setModuleSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Get admin's university_id
        let universityId = currentUser?.university_id;
        
        if (!universityId && currentUser?.id) {
          // If university_id not in user context, get from admins table
          const { data: adminData, error: adminError } = await supabase
            .from('admins')
            .select('university_id')
            .eq('user_id', currentUser.id)
            .single();
          
          if (!adminError && adminData) {
            universityId = adminData.university_id;
          }
        }

        if (!universityId) {
          console.error('Unable to determine university_id for data filtering');
          alert('Unable to determine your university. Please contact support.');
          return;
        }

        console.log('Loading data for university:', universityId);

        // Load modules from Supabase
        const { data: modulesData, error: modulesError } = await supabase
          .from('modules')
          .select('id, module_name, module_code, active')
          .eq('university_id', universityId)
          .eq('active', true)
          .order('module_name', { ascending: true });

        if (modulesError) {
          console.error('Error loading modules:', modulesError);
          throw modulesError;
        }

        console.log(`Loaded ${modulesData?.length || 0} modules for university ${universityId}`);
        setModulesList(modulesData || []);
        setFilteredModules(modulesData || []);

        // Load degree programs from Supabase
        const { data: degreeData, error: degreeError } = await supabase
          .from('degree_details')
          .select('id, degree_programme, local_international, degree_level, active')
          .eq('university_id', universityId)
          .eq('active', true)
          .order('degree_programme', { ascending: true });

        if (degreeError) {
          console.error('Error loading degree programs:', degreeError);
          throw degreeError;
        }

        // Format degree programs for display
        const formattedDegreePrograms = degreeData?.map(degree => ({
          id: degree.id,
          displayName: `${degree.degree_programme} (${degree.local_international}) (${degree.degree_level})`,
          originalData: degree
        })) || [];

        console.log(`Loaded ${formattedDegreePrograms.length} degree programs for university ${universityId}`);
        setDegreeProgramsList(formattedDegreePrograms);
        
      } catch (error) {
        console.error('Error loading data:', error);
        alert('Error loading data: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle module search and selection
  const handleModuleSearch = (e) => {
    const query = e.target.value;
    setModuleSearchQuery(query);
    
    if (query.trim() === '') {
      setFilteredModules(modulesList);
    } else {
      const filtered = modulesList.filter(module => 
        module.module_name.toLowerCase().includes(query.toLowerCase()) ||
        module.module_code.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredModules(filtered);
    }
    setShowModuleDropdown(true);
  };

  const handleModuleSelect = (module) => {
    console.log('Selected module:', module); // Debug log
    setFormData(prev => ({
      ...prev,
      selectedModule: module.module_name,
      selectedModuleId: module.id // Store the actual module ID
    }));
    setModuleSearchQuery(`${module.module_name} (${module.module_code})`);
    setShowModuleDropdown(false);
    
    console.log('Updated formData with module:', {
      selectedModule: module.module_name,
      selectedModuleId: module.id
    });
  };

  // Handle degree program selection (multi-select)
  const handleDegreeProgramChange = (programId) => {
    setFormData(prev => ({
      ...prev,
      selectedDegreePrograms: prev.selectedDegreePrograms.includes(programId)
        ? prev.selectedDegreePrograms.filter(id => id !== programId)
        : [...prev.selectedDegreePrograms, programId]
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: Log current form data
    console.log('Current formData on submit:', formData);
    
    if (!formData.selectedModuleId || formData.selectedDegreePrograms.length === 0 || !formData.year) {
      alert('Please fill in all required fields');
      return;
    }

    if (!currentUser) {
      alert('Please log in to map modules');
      return;
    }

    try {
      setLoading(true);

      // Prepare mapping data for each selected degree program
      const mappingPromises = formData.selectedDegreePrograms.map(async (degreeId) => {
        const mappingData = {
          module_id: formData.selectedModuleId,     // From modules table
          degree_details_id: degreeId,              // From degree_details table  
          year: parseInt(formData.year),            // Year as integer
          is_mandatory: formData.isMandatory,       // Boolean from checkbox
          created_at: new Date().toISOString()
        };

        console.log('Submitting mapping data to module_degree_mappings table:', mappingData);

        // Insert into Supabase module_degree_mappings table
        const { data, error } = await supabase
          .from('module_degree_mappings')
          .insert([mappingData])
          .select()
          .single();

        if (error) {
          console.error('Supabase insert error:', error);
          throw error;
        }

        return data;
      });

      // Wait for all mappings to complete
      const results = await Promise.all(mappingPromises);
      
      console.log('All mappings created successfully:', results);
      alert(`Module mapped to ${formData.selectedDegreePrograms.length} degree program(s) successfully!`);
      navigate('/add-module');

    } catch (error) {
      console.error('Error mapping module:', error);
      let errorMessage = 'Error mapping module: ';
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.details) {
        errorMessage += error.details;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to map modules.
            </p>
            <Link to="/login">
              <button style={{
                padding: '12px 24px',
                background: '#F04915',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontFamily: 'Outfit',
                fontWeight: '500',
                cursor: 'pointer',
                marginTop: '20px'
              }}>
                Go to Login
              </button>
            </Link>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Map Module to Degree Programme</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              {loading && (
                <div style={{textAlign: 'center', marginBottom: '20px'}}>
                  <div style={{color: '#F04915', fontSize: 16, fontFamily: 'Outfit'}}>
                    Loading data...
                  </div>
                </div>
              )}
              
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px', position: 'relative'}} data-module-dropdown>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Select Module <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Search for module..."
                      value={moduleSearchQuery}
                      onChange={handleModuleSearch}
                      onFocus={() => setShowModuleDropdown(true)}
                      data-module-input
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: formData.selectedModuleId ? '2px solid #28a745' : '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: formData.selectedModuleId ? '#f8fff8' : 'white'
                      }}
                    />
                    {formData.selectedModuleId && (
                      <div style={{
                        fontSize: 14,
                        color: '#28a745',
                        marginTop: '4px',
                        fontFamily: 'Outfit'
                      }}>
                        ✓ Selected: {formData.selectedModule} (ID: {formData.selectedModuleId})
                      </div>
                    )}
                    {showModuleDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #77838F',
                        borderTop: 'none',
                        borderRadius: '0 0 8px 8px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}>
                        {filteredModules.length === 0 ? (
                          <div style={{
                            padding: '12px 15px',
                            fontSize: 16,
                            fontFamily: 'Outfit',
                            color: '#888',
                            textAlign: 'center'
                          }}>
                            No modules found for your university
                          </div>
                        ) : (
                          filteredModules.map(module => (
                            <div
                              key={module.id}
                              onClick={() => handleModuleSelect(module)}
                              style={{
                                padding: '12px 15px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: 16,
                                fontFamily: 'Outfit',
                                transition: 'background-color 0.2s',
                                backgroundColor: formData.selectedModuleId === module.id ? '#e3f2fd' : 'white'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = formData.selectedModuleId === module.id ? '#e3f2fd' : 'white';
                              }}
                            >
                              {module.module_name} ({module.module_code})
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Year <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Year</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                      <input
                        type="checkbox"
                        name="isMandatory"
                        checked={formData.isMandatory}
                        onChange={handleInputChange}
                        style={{marginRight: '12px', width: '20px', height: '20px'}}
                      />
                      <span style={{color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Is Mandatory <span style={{color: '#F04915'}}>*</span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Right Column - Degree Programs */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Select Degree Programme(s) <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <div style={{
                      border: '1px solid #77838F',
                      borderRadius: 8,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      background: 'white'
                    }}>
                      {degreeProgramsList.map(program => (
                        <label
                          key={program.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 15px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = formData.selectedDegreePrograms.includes(program.id) ? '#e7f3ff' : 'white';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.selectedDegreePrograms.includes(program.id)}
                            onChange={() => handleDegreeProgramChange(program.id)}
                            style={{marginRight: '12px', width: '16px', height: '16px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>
                            {program.displayName}
                          </span>
                        </label>
                      ))}
                    </div>
                    {formData.selectedDegreePrograms.length > 0 && (
                      <div style={{marginTop: '8px', fontSize: 14, fontFamily: 'Outfit', color: '#666'}}>
                        {formData.selectedDegreePrograms.length} programme(s) selected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{margin: '40px', display: 'flex', justifyContent: 'space-between'}}>
            <Link to="/add-module">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back
              </button>
            </Link>
            <button 
              type="submit" 
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#D63A0F';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F04915';
              }}
            >
              Map Module
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default MapModuleToDegree;
